// API service layer
// Handles all HTTP requests to the backend

import { useAuthStore } from '../store/authStore';

const API_BASE_URL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || 'http://localhost:8787/api/v1';

interface APIError {
  code: string;
  message: string;
  details?: unknown;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: APIError;
  timestamp: string;
}

class APIService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const { accessToken, refreshToken, clearAuth } = useAuthStore.getState();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add auth header if access token exists
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      let response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      // If 401 and we have a refresh token, try to refresh
      if (response.status === 401 && refreshToken) {
        const refreshed = await this.refreshAccessToken(refreshToken);
        if (refreshed) {
          // Retry original request with new token
          headers['Authorization'] = `Bearer ${refreshed}`;
          response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include',
          });
        } else {
          // Refresh failed, clear auth
          clearAuth();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
      }

      const data: APIResponse<T> = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'An error occurred');
      }

      return data.data as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please try again.');
    }
  }

  private async refreshAccessToken(refreshToken: string): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data: APIResponse<{ access_token: string; refresh_token: string }> = await response.json();

      if (data.success && data.data) {
        const { setAccessToken } = useAuthStore.getState();
        setAccessToken(data.data.access_token);
        return data.data.access_token;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  // ============================================
  // AUTH ENDPOINTS
  // ============================================

  async register(email: string, password: string) {
    return this.request<{ user_id: string; email: string; message: string }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    );
  }

  async login(email: string, password: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: {
        user_id: string;
        email: string;
        email_verified: boolean;
        mfa_enabled: boolean;
        created_at: string;
        credit_balance: number;
        total_stories: number;
      };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout(refreshToken: string) {
    return this.request<{ message: string }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  async getCurrentUser() {
    return this.request<{
      user_id: string;
      email: string;
      email_verified: boolean;
      mfa_enabled: boolean;
      created_at: string;
      credit_balance: number;
      total_stories: number;
    }>('/auth/me');
  }

  // ============================================
  // USER ENDPOINTS
  // ============================================

  async getUserProfile() {
    return this.request('/user/profile');
  }

  async updateProfile(data: { email?: string }) {
    return this.request('/user/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAccount() {
    return this.request('/user/account', {
      method: 'DELETE',
    });
  }

  // ============================================
  // CREDIT ENDPOINTS
  // ============================================

  async getCreditBalance() {
    return this.request('/credits/balance');
  }

  async purchaseCredits(amount: number, paymentMethodId: string) {
    return this.request('/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({ amount, payment_method_id: paymentMethodId }),
    });
  }

  async getTransactionHistory() {
    return this.request('/credits/transactions');
  }

  // ============================================
  // STORY ENDPOINTS
  // ============================================

  async createStory(data: {
    title: string;
    story_type: string;
    story_input: string;
    word_count: number;
  }) {
    return this.request<{
      story_id: string;
      title: string;
      status: string;
      credits_used: number;
      new_credit_balance: number;
    }>('/stories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getStories() {
    return this.request('/stories');
  }

  async getStory(storyId: string) {
    return this.request<{
      story_id: string;
      title: string;
      story_type: string;
      word_count: number;
      credits_used: number;
      status: string;
      created_at: string;
      completed_at: string | null;
      generation_error: string | null;
      progress: {
        stage: 'text_generation' | 'audio_generation' | 'uploading';
        progress: number;
        message: string;
      } | null;
      audiobook: {
        audiobook_id: string;
        duration_seconds: number;
        file_size_bytes: number;
        format: string;
        download_count: number;
      } | null;
    }>(`/stories/${storyId}`);
  }

  async deleteStory(storyId: string) {
    return this.request(`/stories/${storyId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // AUDIOBOOK ENDPOINTS
  // ============================================

  async getAudiobook(storyId: string) {
    return this.request(`/audiobooks/${storyId}`);
  }

  async getAudiobookDownloadUrl(storyId: string) {
    return this.request(`/audiobooks/${storyId}/download`);
  }

  async regenerateAudiobook(storyId: string) {
    return this.request(`/audiobooks/${storyId}/regenerate`, {
      method: 'POST',
    });
  }
}

export const api = new APIService();
